/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import webpack_dependency_treeImg from 'assets/images/webpack-dependency-tree.png'
import depsImg from 'assets/images/deps.png'
import bundleImg from 'assets/images/bundle.png'

export const _4 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>4 - Webpack</h2>
    <p className={styles.description}>
      <a className={styles.link} rel="noopener noreferrer" href="https://webpack.js.org/" target="_blank">
        Webpack
      </a>{' '}
      — это сборщик JS-модулей, менеджер модульных зависимостей, который анализирует дерево зависимостей и создает один
      или несколько результирующих файлов, содержащих всю кодовую базу проекта. Выстраивает порядок подключения модулей,
      собирает, минифицирует, запаковывает и многое другое.
    </p>
    <img className={styles.img} src={webpack_dependency_treeImg} alt="webpack-dependency-tree" />
    <p className={styles.description}>
      <b>Webpack</b> стал одним из самых важных инструментов веб-разработчика. В первую очередь это менеджер модульных
      зависимостей приложения и сборщик JS-файлов, но он может трансформировать все ресурсы — HTML и CSS, SASS, LESS и
      PostCSS, оптимизировать изображения, компилировать шаблоны, запускать локальный веб-сервер для разработки и многое
      другое.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.1 - Принцип работы</h3>
      <p className={styles.description}>
        Предположим, у нас есть приложение, которое может выполнять две простые математические задачи: суммировать и
        умножать. Мы решили разделить эти функции на отдельные файлы (модули) для упрощения поддержки кодовой базы.
        Тогда в <code className={styles.code}>index.html</code> скрипты будут подключены в такой последовательности.{' '}
      </p>
      <CopyBlock
        text={`<script src="sum.js"></script>
<script src="multiply.js"></script>
<script src="index.jsx"></script>`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Допустим код из <code className={styles.code}>sum.js</code> используется в{' '}
        <code className={styles.code}>multiply.js</code> и <code className={styles.code}>index.jsx</code>, а код из{' '}
        <code className={styles.code}>multiply.js</code> используется только в{' '}
        <code className={styles.code}>index.jsx</code>. Покажем иерархию зависимостей на простой диаграмме.{' '}
      </p>
      <img className={styles.img} src={depsImg} alt="deps" />
      <p className={styles.description}>
        Если ошибиться в последовательности подключения скриптов в <code className={styles.code}>index.html</code>, то
        есть если <code className={styles.code}>index.jsx</code> включен перед любой из других зависимостей или если{' '}
        <code className={styles.code}>sum.js</code> включен после <code className={styles.code}>multiply.js</code>,
        будут ошибки.{' '}
      </p>
      <p className={styles.description}>
        Теперь предствим, что мы масштабируем это до реального, полностью рабочего приложения — могут быть сотни
        зависимостей. Сохранение порядка подключения станет кошмаром.{' '}
      </p>
      <p className={styles.description}>
        <b>Webpack</b> преобразует зависимости в модули и сошьет в один или несколько файлов. Каждый модуль будет иметь
        закрытое пространство имен и подключаться в нужное время, в правильном порядке.{' '}
      </p>
      <img className={styles.img} src={bundleImg} alt="bundle" />
      <p className={styles.description}>
        <b>Gulp</b> и <b>Grunt</b> все еще занимают достойное место в инструментарии разработчика, и для некоторых
        проектов, функционал <b>Webpack</b> не нужен, хотя он может отлично работать в связке с ними. Тем не менее,
        несмотря на то что кривая обучения может быть выше при более сложных настройках, <b>Webpack</b> незаменим если
        вы используете современные библиотеки и фреймворки для разработки, такие как <b>React</b>, <b>Vue</b>,{' '}
        <b>Angular</b> и т. д.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.2 - Настройка</h3>
      <p className={styles.description}>
        По ссылкам ниже вы найдете исчерпывающие руководства с пошаговыми объяснениями настройки Webpack.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            rel="noopener noreferrer"
            href="https://tproger.ru/translations/configure-webpack4/"
            target="_blank"
          >
            Webpack 4: практические рекомендации по настройке{' '}
          </a>{' '}
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            rel="noopener noreferrer"
            href="https://www.sitepoint.com/webpack-beginner-guide/"
            target="_blank"
          >
            A Beginner’s Guide to Webpack 4 and Module Bundling{' '}
          </a>{' '}
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://wanago.io/2018/07/16/webpack-4-course-part-one-entry-output-and-es6-modules/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Курс по Webpack 4
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://codeburst.io/top-webpack-plugins-for-faster-development-a2f6accb7a3e"
            target="_blank"
            rel="noopener noreferrer"
          >
            Top Webpack plugins for faster development
          </a>
        </li>
        <li className={styles.list__item}>
          <a className={styles.link} href="https://webpack.js.org/concepts/" target="_blank" rel="noopener noreferrer">
            Документация Webpack
          </a>
        </li>
      </ul>
    </section>
  </section>
))
